import { PostData, PostTypes } from '../PostTypes';
import { PostSizes } from '../types/BasePostType';

export interface TemplateInputProps<Data extends PostData = PostData> {
    value: Data;
    onChange?: (data: Data) => void;
}

export interface TemplateDefinition {
    type: PostTypes;
    size: PostSizes;
    inputComponent: React.ComponentType<TemplateInputProps>;
    emptyData: () => PostData;
    isTemplate: (data: PostData) => boolean;
}

export function defineTemplate<
    Data extends Extract<PostData, { $type: Type }>,
    Type extends PostTypes,
    Size extends PostData['size'],
>(
    type: Type,
    size: Size,
    inputComponent: React.ComponentType<TemplateInputProps<Data>>,
    emptyData: () => Data,
    isTemplate: (data: PostData) => data is Data,
) {
    return {
        type,
        size,
        inputComponent: generateInputComponent(
            inputComponent,
            isTemplate,
            emptyData,
        ),
        emptyData,
        isTemplate,
    } satisfies TemplateDefinition;
}

function generateInputComponent<TemplateData extends PostData>(
    Component: React.ComponentType<TemplateInputProps<TemplateData>>,
    isTemplate: (data: PostData) => data is TemplateData,
    emptyData: () => TemplateData,
) {
    return function InputComponent({ value, ...rest }: TemplateInputProps) {
        return (
            <Component
                value={isTemplate(value) ? value : emptyData()}
                {...rest}
            />
        );
    };
}
